import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { LanguageProvider } from "@/context/LanguageContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { WishlistProvider } from "@/context/WishlistContext";
import { CurrencyProvider } from "@/context/CurrencyContext";
import { ThemeProvider } from "@/context/ThemeContext";
import api from "@/lib/api";

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Account from "@/pages/Account";
import Admin from "@/pages/Admin";
import AuthCallback from "@/pages/AuthCallback";
import Careers from "@/pages/Careers";
import JobDetail from "@/pages/JobDetail";
import Wishlist from "@/pages/Wishlist";
import Maintenance from "@/pages/Maintenance";

// Hide the floating "Made with Emergent" badge — we render a discreet inline credit in the footer instead.
function useHideEmergentBadge() {
  React.useEffect(() => {
    const hide = () => {
      const el = document.getElementById("emergent-badge");
      if (el) el.style.setProperty("display", "none", "important");
    };
    hide();
    const observer = new MutationObserver(hide);
    observer.observe(document.body, { childList: true, subtree: false });
    return () => observer.disconnect();
  }, []);
}

// Register the PWA service worker once after the app loads.
function useRegisterSW() {
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    if (!("serviceWorker" in navigator)) return;
    const onLoad = () => {
      navigator.serviceWorker
        .register("/sw.js")
        .catch((err) => console.warn("SW registration failed", err));
    };
    if (document.readyState === "complete") onLoad();
    else window.addEventListener("load", onLoad, { once: true });
    return () => window.removeEventListener("load", onLoad);
  }, []);
}

// Fire one visit-tracking ping per browser session per UTC day for the daily counter,
// plus a per-page-view ping on every route change (deduped per path per session).
function useTrackVisit() {
  const location = useLocation();
  React.useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    const dayKey = "amazypet_visit_" + today;
    const pathKey = "amazypet_path_" + today + "_" + location.pathname;
    const firstOfDay = !sessionStorage.getItem(dayKey);
    const firstForPath = !sessionStorage.getItem(pathKey);
    if (!firstOfDay && !firstForPath) return;

    sessionStorage.setItem(dayKey, "1");
    sessionStorage.setItem(pathKey, "1");

    // Classify page-type from pathname so the admin can group analytics cleanly
    let pageType = "other";
    const p = location.pathname;
    if (p === "/" ) pageType = "home";
    else if (p.startsWith("/product/")) pageType = "product";
    else if (p.startsWith("/shop")) pageType = "shop";
    else if (p === "/cart") pageType = "cart";
    else if (p === "/checkout" || p.startsWith("/checkout/")) pageType = "checkout";
    else if (p === "/wishlist") pageType = "wishlist";
    else if (p.startsWith("/careers")) pageType = "careers";
    else if (p === "/admin") pageType = "admin";
    else if (p === "/account") pageType = "account";

    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/visits/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ path: p, page_type: pageType }),
    }).catch(() => {});
  }, [location.pathname]);
}

function useMaintenanceStatus() {
  const [maintenance, setMaintenance] = React.useState(false);
  const [ready, setReady] = React.useState(false);
  React.useEffect(() => {
    let active = true;
    const load = () => {
      api
        .get("/settings/public")
        .then((r) => {
          if (!active) return;
          setMaintenance(!!r.data?.maintenance_mode);
        })
        .catch(() => {})
        .finally(() => active && setReady(true));
    };
    load();
    // Poll every 30s so non-admin viewers pick up changes without refresh
    const id = setInterval(load, 30000);
    return () => {
      active = false;
      clearInterval(id);
    };
  }, []);
  return { maintenance, ready };
}

function AppRouter() {
  const location = useLocation();
  const { user, loading } = useAuth();
  const { maintenance, ready } = useMaintenanceStatus();
  useHideEmergentBadge();
  useRegisterSW();
  useTrackVisit();
  // Handle Emergent OAuth callback — synchronous check, before normal routing
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  const isAdmin = user?.role === "admin";

  // While settings or user load, render nothing to avoid flashing public content during maintenance
  if (!ready || loading) {
    return <div className="min-h-screen bg-[#F5F2ED] dark:bg-[#171717]" />;
  }

  // Maintenance gate: non-admin visitors only see maintenance page + login route
  if (maintenance && !isAdmin) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="*" element={<Maintenance />} />
      </Routes>
    );
  }

  return (
    <>
      <Navbar />
      <main className="min-h-[60vh]">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/product/:id" element={<ProductDetail />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/checkout/success" element={<CheckoutSuccess />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/account" element={<Account />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/careers/:id" element={<JobDetail />} />
          <Route path="/wishlist" element={<Wishlist />} />
        </Routes>
      </main>
      <Footer />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <CurrencyProvider>
              <CartProvider>
                <WishlistProvider>
                  <BrowserRouter>
                    <AppRouter />
                    <Toaster position="top-center" richColors />
                  </BrowserRouter>
                </WishlistProvider>
              </CartProvider>
            </CurrencyProvider>
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
